<template>
  <div class="px-3">
    <router-link
      v-if="to"
      v-slot="{ isActive }"
      class="group/sidebar flex items-center max-w-full w-full cursor-pointer focus:outline-hidden transition-colors"
      :class="[sharedClassList, { 'py-3': accountButton }]"
      :to="to"
    >
      <div
        v-tooltip="{
          content: tooltipLabel,
          placement: 'left',
          onShow: () => !sidebarExpanded,
          allowHTML: true,
        }"
      >
        <slot name="icon"></slot>
      </div>
      <div
        v-show="sidebarExpanded"
        data-cy="sidebar-button"
        class="text-sm font-medium text-white/80 transition-colors duration-200 group-hover/sidebar:text-white"
        :class="[
          { 'text-white': isActive || activeCondition },
          accountButton
            ? 'grow overflow-hidden py-px pr-6 pl-4 whitespace-nowrap'
            : 'pr-4 pl-2',
        ]"
      >
        <slot name="label"></slot>
      </div>
      <slot />
    </router-link>

    <button
      v-else
      class="group/sidebar items-center relative flex max-w-full w-full cursor-pointer focus:outline-hidden transition-colors"
      :class="[sharedClassList, { 'py-5': accountButton }]"
    >
      <div
        v-tooltip="{
          content: tooltipLabel,
          placement: 'left',
          onShow: () => !sidebarExpanded,
          allowHTML: true,
        }"
      >
        <slot name="icon"></slot>
      </div>
      <div
        v-if="sidebarExpanded"
        class="pr-4 text-sm font-medium group-hover/sidebar:text-white"
        :class="[
          accountButton ? 'pl-4' : 'pl-2',
          insetButton ? 'text-white' : 'text-white/80',
        ]"
      >
        <slot name="label"></slot>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { type PropType, computed } from "vue";

import type { RouteLocationRaw } from "vue-router";

const props = defineProps({
  to: {
    type: [String, Object] as PropType<RouteLocationRaw | string>,
    default: null,
  },
  sidebarExpanded: {
    type: Boolean,
    default: false,
  },
  accountButton: {
    type: Boolean,
    default: false,
  },
  insetButton: {
    type: Boolean,
    default: false,
  },
  insetColor: {
    type: String,
    default: "",
  },
  tooltipLabel: {
    type: String,
    default: "",
  },
  activeCondition: {
    type: Boolean,
    default: false,
  },
});

const sharedClassList = computed(() => {
  const classes: Record<string, boolean | string> = {
    "justify-left": props.sidebarExpanded,
    "justify-center": !props.sidebarExpanded,
    "border-t border-white/10": props.accountButton,
    rounded: props.insetButton,
    "rounded-sm": !props.insetButton && !props.accountButton,
    "hover:bg-white/10": !props.insetColor,
    [props.insetColor]: props.insetColor,
    "py-2": props.sidebarExpanded && props.insetButton,
    "pl-4": props.sidebarExpanded && props.accountButton,
    "router-link-active": props.activeCondition,
  };
  return Object.keys(classes)
    .filter((key) => classes[key])
    .join(" ");
});
</script>

<style scoped>
@reference "@/assets/tailwind.css";

@reference "@/assets/tailwind.css";

.router-link-active {
  @apply bg-white/10;
}
</style>
